<template>
  <div class="counter-display-container">
    <!-- Header with Logo - Always visible -->
    <div class="counter-header">
      <img :src="logoSrc" alt="Logo" class="header-logo" />
    </div>
    
    <div v-if="counterName" class="counter-display">
      <div class="counter-card">
        <!-- Main Content Area -->
        <div class="counter-content" :class="{ 'no-ticket': !ticketNumber }" :style="!ticketNumber ? { background: guichetPanelBackground } : {}">
          <!-- Show Counter Number when no ticket -->
          <div v-if="!ticketNumber" class="counter-number-large">
            <div class="counter-label" :style="{ color: guichetPanelTextColor }">{{ displayConfig.CounterName || 'Guichet' }}</div>
            <div class="counter-number-value" :style="{ color: guichetPanelTextColor }">{{ counterNumber }}</div>
          </div>
          <!-- Waiting / placeholder "-": full-width Guichet only (no ticket column) -->
          <div
            v-else-if="showGuichetOnly"
            class="guichet-only-layout"
            :style="{ background: guichetPanelBackground }"
          >
            <div class="counter-name-display">
              <div class="counter-label-text" :style="{ color: guichetPanelTextColor }">{{ displayConfig.CounterName || 'Guichet' }}</div>
              <div class="counter-number-text guichet-only-number" :style="{ color: guichetPanelTextColor }">{{ counterNumber }}</div>
            </div>
          </div>
          <!-- Show Split Layout when a real ticket code is shown -->
          <div v-else class="split-layout">
            <!-- Left Section: Counter Name -->
            <div
              class="left-section"
              :style="{ background: guichetPanelBackground }"
            >
              <div class="counter-name-display">
                <div class="counter-label-text" :style="{ color: guichetPanelTextColor }">{{ displayConfig.CounterName || 'Guichet' }}</div>
                <div class="counter-number-text" :style="{ color: guichetPanelTextColor }">{{ counterNumber }}</div>
              </div>
            </div>
            
            <!-- Vertical Divider -->
            
            <!-- Right Section: Ticket Number -->
            <div class="right-section" :style="{ background: ticketPanelBackground }">
              <div class="ticket-display">
                <div class="ticket-label-text" :style="{ color: ticketPanelTextColor }">TICKET</div>
                <div class="ticket-number" ref="ticketNumberRef" :style="{ fontSize: computedFontSize, color: ticketPanelTextColor }">{{ displayTicketText }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="no-counter-message" :style="{ background: counterCloseBackground }">
      <div class="no-counter-text" :style="{ color: counterCloseTextColor }">{{ $t('Guichet fermé') || 'Guichet fermé' }}</div>
    </div>
  </div>
</template>

<script>
import fallbackLogo from '@/assets/images/liberrex.png'

const COUNTER_OPEN_COLOR_DEFAULT = '#008fca'
const TICKET_PANEL_FALLBACK = '#ffffff'
const COUNTER_CLOSE_COLOR_DEFAULT = '#ffffff'

function parseHexToRgb(s) {
  if (s[0] !== '#') {
    return null;
  }
  let h = s.slice(1);
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('');
  }
  if (h.length === 6 || h.length === 8) {
    const n = parseInt(h.slice(0, 6), 16);
    if (!Number.isNaN(n)) {
      return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
    }
  }
  return null;
}

/** rgb() / rgba() with integers, decimals, or % channels. */
function parseRgbFunctionToRgb(input) {
  const m = input.trim().match(/^rgba?\(\s*([^)]+)\s*\)/i);
  if (!m) {
    return null;
  }
  const parts = m[1].split(/\s*,\s*/).map((p) => p.trim());
  if (parts.length < 3) {
    return null;
  }
  const parseCh = (p) => {
    const t = String(p).trim();
    if (t.endsWith('%')) {
      const v = parseFloat(t);
      if (Number.isNaN(v)) {
        return NaN;
      }
      return Math.round((v / 100) * 255);
    }
    const v = parseFloat(t);
    if (Number.isNaN(v)) {
      return NaN;
    }
    return Math.min(255, Math.max(0, Math.round(v)));
  };
  const r = parseCh(parts[0]);
  const g = parseCh(parts[1]);
  const b = parseCh(parts[2]);
  if ([r, g, b].some((x) => Number.isNaN(x))) {
    return null;
  }
  return { r, g, b };
}

/** hsl() / hsla() → sRGB 0–255. */
function parseHslFunctionToRgb(input) {
  const m = input.trim().match(/^hsla?\(\s*([^)]+)\s*\)/i);
  if (!m) {
    return null;
  }
  const parts = m[1].split(/\s*,\s*/).map((p) => p.trim());
  if (parts.length < 3) {
    return null;
  }
  const hRaw = parseFloat(parts[0]);
  const sRaw = parseFloat(String(parts[1]).replace(/%/g, '').trim());
  const lRaw = parseFloat(String(parts[2]).replace(/%/g, '').trim());
  if ([hRaw, sRaw, lRaw].some((x) => Number.isNaN(x))) {
    return null;
  }
  const h = (((hRaw % 360) + 360) % 360) / 360;
  const sat = Math.min(100, Math.max(0, sRaw)) / 100;
  const light = Math.min(100, Math.max(0, lRaw)) / 100;
  if (sat === 0) {
    const v = Math.round(light * 255);
    return { r: v, g: v, b: v };
  }
  const hue2rgb = (p, q, t) => {
    let tt = t;
    if (tt < 0) {
      tt += 1;
    }
    if (tt > 1) {
      tt -= 1;
    }
    if (tt < 1 / 6) {
      return p + (q - p) * 6 * tt;
    }
    if (tt < 1 / 2) {
      return q;
    }
    if (tt < 2 / 3) {
      return p + (q - p) * (2 / 3 - tt) * 6;
    }
    return p;
  };
  const q = light < 0.5 ? light * (1 + sat) : light + sat - light * sat;
  const p = 2 * light - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return { r, g, b };
}

/** Let the browser resolve any valid CSS color (named colors, hsl, etc.). */
function parseCssColorViaBrowser(cssColor) {
  if (typeof document === 'undefined' || !document.body) {
    return null;
  }
  try {
    const el = document.createElement('div');
    el.style.cssText =
      'position:absolute;left:-9999px;top:0;width:1px;height:1px;visibility:hidden;pointer-events:none';
    el.style.backgroundColor = '';
    el.style.backgroundColor = cssColor;
    if (!el.style.backgroundColor) {
      return null;
    }
    document.body.appendChild(el);
    const bg = getComputedStyle(el).backgroundColor;
    document.body.removeChild(el);
    const rm = bg.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/);
    if (!rm) {
      return null;
    }
    const a = rm[4] !== undefined ? parseFloat(rm[4]) : 1;
    if (a < 0.02) {
      return null;
    }
    return {
      r: Math.round(parseFloat(rm[1])),
      g: Math.round(parseFloat(rm[2])),
      b: Math.round(parseFloat(rm[3]))
    };
  } catch {
    return null;
  }
}

/** Resolve a CSS background color string to sRGB (opaque). */
function resolveBackgroundToRgb(input) {
  if (input == null || typeof input !== 'string') {
    return null;
  }
  const s = input.trim();
  if (!s) {
    return null;
  }
  if (s[0] === '#') {
    const hex = parseHexToRgb(s);
    if (hex) {
      return hex;
    }
  }
  if (/^[0-9a-fA-F]{6}$/.test(s)) {
    const n = parseInt(s, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  const lower = s.toLowerCase();
  if (lower.startsWith('rgb')) {
    const rgb = parseRgbFunctionToRgb(s);
    if (rgb) {
      return rgb;
    }
  }
  if (lower.startsWith('hsl')) {
    const hsl = parseHslFunctionToRgb(s);
    if (hsl) {
      return hsl;
    }
  }
  return parseCssColorViaBrowser(s);
}

function relativeLuminance({ r, g, b }) {
  const lin = [r, g, b].map((c) => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/**
 * Black or white from background: YIQ perceived brightness + WCAG luminance
 * so light pastels (e.g. pink) get dark text.
 */
function contrastTextOnBackground(bgCss) {
  const rgb = resolveBackgroundToRgb(bgCss);
  if (!rgb) {
    return '#000000';
  }
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  const L = relativeLuminance(rgb);
  const lightByYiq = yiq >= 128;
  const lightByLuma = L > 0.5;
  const useDarkText = lightByYiq || lightByLuma;
  return useDarkText ? '#000000' : '#FFFFFF';
}

export default {
  name: 'CounterDisplay',
  data() {
    return {
      allStorage: {},
      currentTicket: null,
      ticketVersion: 0,
      fontSize: null,
      displayConfig: {
        counterOpenColor: COUNTER_OPEN_COLOR_DEFAULT,
        counterCloseColor: COUNTER_CLOSE_COLOR_DEFAULT,
        GuichetNumberColor: null,
        NumberColor: null,
        CounterName: 'Guichet',
        logo: null
      }
    }
  },
  computed: {
    counterName() {
      console.log("this.all s",this.allStorage)
      // Extract counter name from the stored object
      if (this.allStorage && 
          typeof this.allStorage === 'object' && 
          !Array.isArray(this.allStorage) &&
          Object.keys(this.allStorage).length > 0 &&
          this.allStorage.name) {
        return this.allStorage.name;
      }
      return null;
    },
    counterNumber() {
      // Extract counter number from name or id
      return this.allStorage.name;

    },
    ticketNumber() {
      // Depend on ticketVersion so Vue re-computes when we manually bump it
      void this.ticketVersion;
      // Read the ticket number directly from localStorage
      try {
        const raw = localStorage.getItem('public_identifier');
        if (raw === null || raw === undefined || raw === '') {
          return null;
        }
        // It may be JSON-stringified (we set it with JSON.stringify)
        let value = raw;
        try {
          value = JSON.parse(raw);
        } catch (_) {
          // keep raw value if not JSON
        }
        const cleaned = String(value).replace(/\s/g, '');
        return cleaned || null;
      } catch {
        return null;
      }
    },
    ticketStatusRaw() {
      void this.ticketVersion;
      try {
        return (localStorage.getItem('public_ticket_status') || '').trim();
      } catch {
        return '';
      }
    },
    isTicketWaiting() {
      return this.ticketStatusRaw.toLowerCase() === 'waiting';
    },
    /** Shown on the public counter display; hide identifier while status is waiting. */
    displayTicketText() {
      if (!this.ticketNumber) {
        return null;
      }
      if (this.isTicketWaiting) {
        return '-';
      }
      return this.ticketNumber;
    },
    /** Ticket panel shows "-": use single full-width Guichet block instead of split view. */
    showGuichetOnly() {
      return this.displayTicketText === '-';
    },
    serviceInfo() {
      if (this.currentTicket && this.currentTicket.service_info) {
        return this.currentTicket.service_info;
      }
      return null;
    },
    computedFontSize() {
      if (this.fontSize) {
        return this.fontSize;
      }
      // Default fallback font size
      return 'clamp(8rem, 15vw, 20rem)';
    },
    /** When counterOpenColor is missing, empty, or whitespace-only, use default. */
    counterOpenBackground() {
      const c = this.displayConfig.counterOpenColor;
      if (c == null) {
        return COUNTER_OPEN_COLOR_DEFAULT;
      }
      const s = String(c).trim();
      return s || COUNTER_OPEN_COLOR_DEFAULT;
    },
    /** Left / guichet panel background from GuichetNumberColor, else fixed fallback. */
    guichetPanelBackground() {
      const c = this.displayConfig.GuichetNumberColor;
      if (c == null) {
        return COUNTER_OPEN_COLOR_DEFAULT;
      }
      const s = String(c).trim();
      return s || COUNTER_OPEN_COLOR_DEFAULT;
    },
    /** Right / ticket panel background from NumberColor. */
    ticketPanelBackground() {
      const c = this.displayConfig.NumberColor;
      if (c == null) {
        return TICKET_PANEL_FALLBACK;
      }
      const s = String(c).trim();
      return s || TICKET_PANEL_FALLBACK;
    },
    guichetPanelTextColor() {
      return contrastTextOnBackground(this.guichetPanelBackground);
    },
    ticketPanelTextColor() {
      return contrastTextOnBackground(this.ticketPanelBackground);
    },
    counterCloseBackground() {
      const c = this.displayConfig.counterCloseColor;
      if (c == null) {
        return COUNTER_CLOSE_COLOR_DEFAULT;
      }
      const s = String(c).trim();
      return s || COUNTER_CLOSE_COLOR_DEFAULT;
    },
    counterCloseTextColor() {
      return contrastTextOnBackground(this.counterCloseBackground);
    },
    /** Base64 or data-URL from displayConfig.logo; falls back to bundled image. */
    logoSrc() {
      const logo = this.displayConfig.logo;
      if (logo == null || typeof logo !== 'string') {
        return fallbackLogo;
      }
      const t = logo.trim();
      if (!t) {
        return fallbackLogo;
      }
      if (t.startsWith('data:')) {
        return t;
      }
      if (t.startsWith('iVBOR')) {
        return `data:image/png;base64,${t}`;
      }
      if (t.startsWith('/9j/')) {
        return `data:image/jpeg;base64,${t}`;
      }
      if (t.startsWith('R0lGOD')) {
        return `data:image/gif;base64,${t}`;
      }
      if (t.startsWith('PHN2Zy')) {
        return `data:image/svg+xml;base64,${t}`;
      }
      return `data:image/png;base64,${t}`;
    }
  },
  methods: {
    loadDisplayConfig() {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          // Use defaults if no user found
          return;
        }
        
        const user = JSON.parse(userStr);
        if (user && user.business && user.business.teller_display_config) {
          const configStr = user.business.teller_display_config;
          let config;
          
          // Parse the config string (it might be a JSON string)
          if (typeof configStr === 'string') {
            try {
              config = JSON.parse(configStr);
            } catch (e) {
              console.error('Error parsing teller_display_config:', e);
              return;
            }
          } else {
            config = configStr;
          }

          if (!config || typeof config !== 'object') {
            return;
          }

          // Update display config with parsed values, keeping defaults if missing
          const logoRaw = config.logo;
          const logo =
            logoRaw != null &&
            String(logoRaw).trim() !== ''
              ? String(logoRaw).trim()
              : null;
          const openRaw = config.counterOpenColor;
          const counterOpenColor =
            openRaw != null && String(openRaw).trim() !== ''
              ? String(openRaw).trim()
              : COUNTER_OPEN_COLOR_DEFAULT;
          const guichetNumRaw = config.GuichetNumberColor;
          const GuichetNumberColor =
            guichetNumRaw != null && String(guichetNumRaw).trim() !== ''
              ? String(guichetNumRaw).trim()
              : null;
          const ticketNumRaw = config.NumberColor;
          const NumberColor =
            ticketNumRaw != null && String(ticketNumRaw).trim() !== ''
              ? String(ticketNumRaw).trim()
              : null;
          const closeRaw = config.counterCloseColor;
          const counterCloseColor =
            closeRaw != null && String(closeRaw).trim() !== ''
              ? String(closeRaw).trim()
              : COUNTER_CLOSE_COLOR_DEFAULT;
          this.displayConfig = {
            counterOpenColor,
            counterCloseColor,
            GuichetNumberColor,
            NumberColor,
            CounterName: config.CounterName || 'Guichet',
            logo
          };
        }
      } catch (e) {
        console.error('Error loading display config:', e);
        // Keep default values on error
      }
    },
    updateStorage() {
      try {
        const counterStr = localStorage.getItem("counter");
        
        if (!counterStr || counterStr === '{}' || counterStr === '""' || counterStr.trim() === '') {
          this.allStorage = {};
          this.currentTicket = null;
          return;
        }
        
        let result = JSON.parse(counterStr);
        
        // Handle case where it might be a string '{}' (double stringified)
        if (typeof result === 'string') {
          if (result === '{}' || result.trim() === '') {
            this.allStorage = {};
            this.currentTicket = null;
            return;
          }
          try {
            result = JSON.parse(result);
          } catch (e) {
            this.allStorage = {};
            this.currentTicket = null;
            return;
          }
        }
        
        // Check if it's a valid counter object
        if (result && 
            typeof result === 'object' && 
            !Array.isArray(result) &&
            Object.keys(result).length > 0) {
          this.allStorage = result;
          // We no longer use sockets; ticket comes from localStorage only
        } else {
          this.allStorage = {};
          this.currentTicket = null;
        }
      } catch (e) {
        console.error('Error parsing counter from localStorage:', e);
        this.allStorage = {};
        this.currentTicket = null;
      }
    },
    calculateFontSize() {
      this.$nextTick(() => {
        if (!this.displayTicketText || !this.$refs.ticketNumberRef) {
          this.fontSize = null;
          return;
        }
        
        const ticketElement = this.$refs.ticketNumberRef;
        const container = ticketElement.closest('.right-section'); // Get the right section container
        
        if (!container || !ticketElement || container.clientWidth === 0 || container.clientHeight === 0) {
          return;
        }
        
        // Get container dimensions (the right section is half the width)
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        
        // Reserve some padding (10% on each side)
        const maxWidth = containerWidth * 0.8;
        const maxHeight = containerHeight * 0.8;
        
        // Estimate font size based on text length and container size
        const textLength = this.displayTicketText.length;
        let baseFontSize;
        
        if (textLength <= 3) {
          // Short numbers: can use more of the container
          baseFontSize = Math.min(maxWidth / (textLength * 0.6), maxHeight * 0.8);
        } else {
          // Longer text: need to account for more characters
          baseFontSize = Math.min(maxWidth / (textLength * 0.5), maxHeight * 0.7);
        }
        
        // Create a temporary element to measure actual text dimensions
        const tempElement = document.createElement('div');
        tempElement.style.position = 'absolute';
        tempElement.style.visibility = 'hidden';
        tempElement.style.whiteSpace = 'nowrap';
        tempElement.style.fontFamily = 'sans-serif';
        tempElement.style.fontWeight = '700';
        tempElement.style.fontSize = baseFontSize + 'px';
        tempElement.textContent = this.displayTicketText;
        document.body.appendChild(tempElement);
        
        const textWidth = tempElement.offsetWidth;
        const textHeight = tempElement.offsetHeight;
        
        // Calculate scale factor if text is too large
        const widthScale = maxWidth / textWidth;
        const heightScale = maxHeight / textHeight;
        const scale = Math.min(widthScale, heightScale, 1); // Don't scale up, only down
        
        let fontSize = baseFontSize * scale;
        
        // Ensure reasonable bounds
        const minSize = Math.min(maxWidth * 0.15, maxHeight * 0.15, 48); // At least 48px
        const maxSize = Math.min(maxWidth * 0.9, maxHeight * 0.9);
        fontSize = Math.max(minSize, Math.min(fontSize, maxSize));
        
        // Clean up
        document.body.removeChild(tempElement);
        
        // Set the calculated font size
        this.fontSize = fontSize + 'px';
      });
    },
    
  },
  watch: {
    displayTicketText() {
      this.calculateFontSize();
    }
  },
  mounted() {
    // Initial load
    this.loadDisplayConfig();
    this.updateStorage();
    // Listen for storage changes from other windows
    this.handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === null) {
        this.loadDisplayConfig();
      }
      if (e.key === 'counter' || e.key === null) {
        this.updateStorage();
      }
      if (
        e.key === 'public_identifier' ||
        e.key === 'public_ticket_status' ||
        e.key === null
      ) {
        // bump version to trigger computed update
        this.ticketVersion++;
      }
    };

    window.addEventListener('storage', this.handleStorageChange);

    // Listen to a same-window custom event for immediate updates
    this.handleLocalTicketUpdate = () => {
      this.ticketVersion++;
    };
    window.addEventListener('public-identifier-updated', this.handleLocalTicketUpdate);
    
    // Calculate font size on mount and window resize
    this.calculateFontSize();
    window.addEventListener('resize', this.calculateFontSize);
  },
  beforeUnmount() {
    // Clean up event listener
    if (this.handleStorageChange) {
      window.removeEventListener('storage', this.handleStorageChange);
    }
    if (this.handleLocalTicketUpdate) {
      window.removeEventListener('public-identifier-updated', this.handleLocalTicketUpdate);
    }
    window.removeEventListener('resize', this.calculateFontSize);
  }
}
</script>

<style scoped>
.counter-display-container {
  width: 100%;
  height: 100vh;
  background: white;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.counter-display {
  width: 100%;
  flex: 1;
  animation: fadeIn 0.5s ease-in;
  display: flex;
  flex-direction: column;
}

/* Main Card with Shadow - Full Screen */
.counter-card {
  width: 100%;
  flex: 1;
  background: white;
  border-radius: 0;
  box-shadow: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Image Header Band - Only shown when ticket exists */
.counter-header {
  width: 100%;
  background: white;
  padding: clamp(0.5rem, 1vw, 1rem) clamp(1rem, 2vw, 2rem);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-logo {
  max-height: clamp(160px, 5vh, 70px);
  max-width: 80%;
  height: auto;
  width: auto;
  object-fit: contain;
}

.counter-header-text {
  font-size: clamp(2rem, 5vw, 4rem);
  font-weight: 700;
  color: white;
  text-align: center;
  letter-spacing: 0.05em;
  font-family: sans-serif;
}

/* Main Content Area */
.counter-content {
  flex: 1;
  background: white;
  padding: 0;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Full-width Guichet when ticket display is "-" (e.g. waiting) */
.guichet-only-layout {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding: clamp(2rem, 4vw, 4rem);
  box-sizing: border-box;
}

.guichet-only-layout .counter-name-display {
  justify-content: center;
}

.guichet-only-number {
  font-size: clamp(8rem, 22vw, 34rem);
}

/* Split Layout for Counter Name and Ticket Number */
.split-layout {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: stretch;
}

/* Left Section: Counter Name */
.left-section {
  width: 50%;
  flex: 0 0 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(2rem, 4vw, 4rem);
  position: relative;
}

.counter-name-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(0rem, 1vw, 1.5rem);
  height: 100%;
  width: 100%;
}

.counter-label-text {
  font-size: clamp(1.5rem, 4vw, 5rem);
  font-weight: 400;
  color: #FFFFFF;
  text-align: center;
  font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  line-height: 1;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin: 0;
  padding: 0;
}

.counter-number-text {
  font-size: clamp(6rem, 20vw, 30rem);
  font-weight: 900;
  color: #FFFFFF;
  text-align: center;
  font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  line-height: 0.9;
  letter-spacing: -0.03em;
  margin: 0;
  padding: 0;
}

/* Vertical Divider */
.vertical-divider {
  width: 4px;
  background: #1A1A1A;
  flex-shrink: 0;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
}

/* Right Section: Ticket Number */
.right-section {
  width: 50%;
  flex: 0 0 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(2rem, 4vw, 4rem);
  position: relative;
}

/* When no ticket, show counter number prominently - full screen */
.counter-content.no-ticket {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* Background color is set dynamically via inline styles */
}

/* Large Counter Number - displayed when no ticket */
.counter-number-large {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
}

.counter-label {
  font-size: clamp(5rem, 8vw, 12rem);
  font-weight: 700;
  line-height: 1;
  color: white;
  text-align: center;
  font-family: sans-serif;
  margin-bottom: 1rem;
}

.counter-number-value {
  font-size: clamp(25rem, 15vw, 25rem);
  font-weight: 700;
  line-height: 1;
  color: white;
  text-align: center;
  font-family: sans-serif;
}

/* Ticket Display Container */
.ticket-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(0rem, 1vw, 1.5rem);
  height: 100%;
  width: 100%;
}

.ticket-label-text {
  font-size: clamp(1.5rem, 4vw, 5rem);
  font-weight: 400;
  color: #FFFFFF;
  text-align: center;
  font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  line-height: 1;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin: 0;
  padding: 0;
}

/* Large Ticket Number */
.ticket-number {
  font-size: clamp(6rem, 20vw, 30rem);
  font-weight: 900;
  line-height: 0.9;
  color: #FFFFFF;
  text-align: center;
  font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  margin: 0;
  padding: 0;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  white-space: nowrap;
  letter-spacing: -0.02em;
  text-transform: uppercase;
}

/* Service Info in Bottom Right */
.service-info {
  position: absolute;
  bottom: clamp(1.5rem, 3vw, 2.5rem);
  right: clamp(1.5rem, 3vw, 2.5rem);
  font-size: clamp(1.2rem, 2.5vw, 2rem);
  font-weight: 600;
  color: #4CAF50;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-family: sans-serif;
}

/* No Counter Message */
.no-counter-message {
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  animation: fadeIn 0.5s ease-in;
  padding: 2rem;
  /* Background color is set dynamically via inline styles */
}

.no-counter-text {
  font-size: clamp(10rem, 7vw, 25rem);
  font-weight: 700;
  letter-spacing: 0.05em;
  color: white;
  font-family: sans-serif;
  line-height: 1;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
